/**
 * ARIA Change Password Modal
 *
 * Self-service password change for the logged-in user (ARIA auth Option B —
 * see AUTH_ENTRA_ID_SSO_PLAN.md). Requires the current password; no
 * "forgot password" flow exists yet (would need email delivery).
 */

import { useState, type FormEvent } from 'react';
import { useMutation, gql } from '@apollo/client';
import { X, CheckCircle } from 'lucide-react';

const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

interface ChangePasswordModalProps {
  onClose: () => void;
}

// Callers should conditionally mount this (`{show && <ChangePasswordModal ... />}`)
// rather than always rendering with an isOpen prop — useMutation would otherwise
// run on every parent render (e.g. Sidebar's) whether or not the modal is shown.
export function ChangePasswordModal({ onClose }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [mismatchError, setMismatchError] = useState(false);

  const [changePassword, { loading, error }] = useMutation(CHANGE_PASSWORD_MUTATION);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setSuccess(false);
    setMismatchError(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setMismatchError(false);

    if (newPassword !== confirmPassword) {
      setMismatchError(true);
      return;
    }

    try {
      await changePassword({ variables: { currentPassword, newPassword } });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
    } catch {
      // surfaced via `error` below
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
            <button onClick={handleClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {success ? (
            <div className="p-6 space-y-4">
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Password changed successfully.
              </div>
              <div className="flex justify-end">
                <button onClick={handleClose} className="btn-primary">
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current password</label>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  className="input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">At least 8 characters.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm new password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {mismatchError && <p className="text-sm text-red-600">New passwords don't match.</p>}
              {error && <p className="text-sm text-red-600">{error.message}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={handleClose} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="btn-primary">
                  {loading ? 'Changing…' : 'Change password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
